// apps/spellingheroweb/src/app/pages/AdminSettings.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Avatar,
  Tab,
  Tabs,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AdminNavbar from '../../components/Navbar/AdminNavbar';
import Footer from '../../components/Footer/Footer';

// Icons
import SettingsIcon from '@mui/icons-material/Settings';
import SchoolIcon from '@mui/icons-material/School';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BuildIcon from '@mui/icons-material/Build';
import BackupIcon from '@mui/icons-material/Backup';
import LanguageIcon from '@mui/icons-material/Language';

// Firebase
import { onAuthStateChanged } from 'firebase/auth';
import { authen } from '../../firebase/firebase';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';

// Brand colors
const BRAND_PRIMARY = '#2A9235';
const BRAND_SECONDARY = '#4CAF50';
const BG_TINT = '#f8fafc';

interface SchoolSettings {
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  phone: string;
  email: string;
  website?: string;
}

const AdminSettings: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [editing, setEditing] = useState(false);
  const [schoolSettings, setSchoolSettings] = useState<SchoolSettings>({
    name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    phone: '',
    email: '',
    website: '',
  });
  const [tempSettings, setTempSettings] =
    useState<SchoolSettings>(schoolSettings);

  useEffect(() => {
    const db = getFirestore();

    const unsub = onAuthStateChanged(authen, async (user) => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Get admin school info
        const caRef = doc(db, 'custom_auth', user.uid);
        const caSnap = await getDoc(caRef);

        if (caSnap.exists()) {
          const caData = caSnap.data();
          const schoolInfo = caData?.school_info || {};

          const settings: SchoolSettings = {
            name: schoolInfo.name || 'Your School',
            address: schoolInfo.address || '',
            city: schoolInfo.city || '',
            state: schoolInfo.state || '',
            country: schoolInfo.country || '',
            phone: caData?.phone || '',
            email: caData?.email || '',
            website: schoolInfo.website || '',
          };

          setSchoolSettings(settings);
          setTempSettings(settings);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading settings:', error);
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEdit = () => {
    setEditing(true);
    setTempSettings(schoolSettings);
  };

  const handleCancel = () => {
    setEditing(false);
    setTempSettings(schoolSettings);
  };

  const handleSave = async () => {
    // Here you would save to Firebase
    setSchoolSettings(tempSettings);
    setEditing(false);
    // TODO: Update Firebase document
  };

  const handleInputChange = (field: keyof SchoolSettings, value: string) => {
    setTempSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <Box
        sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
      >
        <AdminNavbar />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={60} sx={{ color: BRAND_PRIMARY }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f5f5f5',
        }}
      >
        <AdminNavbar />

        <Box sx={{ flex: 1, pt: { xs: 10, md: 12 }, pb: { xs: 6, md: 8 } }}>
          <Container maxWidth="xl">
            {/* Header */}
            <Box sx={{ mb: 4 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: BRAND_PRIMARY,
                    width: 48,
                    height: 48,
                  }}
                >
                  <SettingsIcon sx={{ color: '#fff', fontSize: 24 }} />
                </Avatar>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: BRAND_PRIMARY,
                    }}
                  >
                    Admin Settings
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Manage your school settings and preferences
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: BRAND_PRIMARY,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: BRAND_PRIMARY,
                  },
                }}
              >
                <Tab label="School Information" />
                <Tab label="User Management" />
                <Tab label="Notifications" />
                <Tab label="Security" />
                <Tab label="System" />
              </Tabs>
            </Paper>

            {/* Tab Content */}
            {tabValue === 0 && (
              <Card elevation={1} sx={{ bgcolor: '#fff' }}>
                <CardContent sx={{ p: 4 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 3 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(BRAND_PRIMARY, 0.1),
                          width: 40,
                          height: 40,
                        }}
                      >
                        <SchoolIcon sx={{ color: BRAND_PRIMARY }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        School Information
                      </Typography>
                    </Stack>

                    {!editing ? (
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={handleEdit}
                        sx={{
                          borderColor: BRAND_PRIMARY,
                          color: BRAND_PRIMARY,
                          '&:hover': {
                            borderColor: BRAND_PRIMARY,
                            bgcolor: alpha(BRAND_PRIMARY, 0.05),
                          },
                        }}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleSave}
                          sx={{
                            bgcolor: BRAND_PRIMARY,
                            '&:hover': { bgcolor: BRAND_SECONDARY },
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    )}
                  </Stack>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="School Name"
                        value={
                          editing ? tempSettings.name : schoolSettings.name
                        }
                        onChange={(e) =>
                          handleInputChange('name', e.target.value)
                        }
                        disabled={!editing}
                        InputProps={{
                          startAdornment: (
                            <SchoolIcon sx={{ color: BRAND_PRIMARY, mr: 1 }} />
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={
                          editing ? tempSettings.email : schoolSettings.email
                        }
                        onChange={(e) =>
                          handleInputChange('email', e.target.value)
                        }
                        disabled={!editing}
                        InputProps={{
                          startAdornment: (
                            <EmailIcon sx={{ color: BRAND_PRIMARY, mr: 1 }} />
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={
                          editing ? tempSettings.phone : schoolSettings.phone
                        }
                        onChange={(e) =>
                          handleInputChange('phone', e.target.value)
                        }
                        disabled={!editing}
                        InputProps={{
                          startAdornment: (
                            <PhoneIcon sx={{ color: BRAND_PRIMARY, mr: 1 }} />
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Website"
                        value={
                          editing
                            ? tempSettings.website
                            : schoolSettings.website
                        }
                        onChange={(e) =>
                          handleInputChange('website', e.target.value)
                        }
                        disabled={!editing}
                        InputProps={{
                          startAdornment: (
                            <LanguageIcon
                              sx={{ color: BRAND_PRIMARY, mr: 1 }}
                            />
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        multiline
                        rows={2}
                        value={
                          editing
                            ? tempSettings.address
                            : schoolSettings.address
                        }
                        onChange={(e) =>
                          handleInputChange('address', e.target.value)
                        }
                        disabled={!editing}
                        InputProps={{
                          startAdornment: (
                            <LocationOnIcon
                              sx={{ color: BRAND_PRIMARY, mr: 1, mt: 1 }}
                            />
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="City"
                        value={
                          editing ? tempSettings.city : schoolSettings.city
                        }
                        onChange={(e) =>
                          handleInputChange('city', e.target.value)
                        }
                        disabled={!editing}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="State"
                        value={
                          editing ? tempSettings.state : schoolSettings.state
                        }
                        onChange={(e) =>
                          handleInputChange('state', e.target.value)
                        }
                        disabled={!editing}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Country"
                        value={
                          editing
                            ? tempSettings.country
                            : schoolSettings.country
                        }
                        onChange={(e) =>
                          handleInputChange('country', e.target.value)
                        }
                        disabled={!editing}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {tabValue === 1 && (
              <Card elevation={1} sx={{ bgcolor: '#fff' }}>
                <CardContent sx={{ p: 4 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ mb: 3 }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha(BRAND_PRIMARY, 0.1),
                        width: 40,
                        height: 40,
                      }}
                    >
                      <AdminPanelSettingsIcon sx={{ color: BRAND_PRIMARY }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      User Management
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 300,
                      textAlign: 'center',
                      bgcolor: alpha(BRAND_PRIMARY, 0.02),
                      borderRadius: 2,
                      p: 4,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha(BRAND_PRIMARY, 0.1),
                        width: 80,
                        height: 80,
                        mb: 2,
                      }}
                    >
                      <BuildIcon sx={{ color: BRAND_PRIMARY, fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      User Management System
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      Admin user management, role assignments, and permissions
                      will be available here
                    </Typography>
                    <Chip
                      label="Coming Soon"
                      sx={{
                        bgcolor: alpha(BRAND_PRIMARY, 0.1),
                        color: BRAND_PRIMARY,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {tabValue === 2 && (
              <Card elevation={1} sx={{ bgcolor: '#fff' }}>
                <CardContent sx={{ p: 4 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ mb: 3 }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha(BRAND_SECONDARY, 0.1),
                        width: 40,
                        height: 40,
                      }}
                    >
                      <NotificationsIcon sx={{ color: BRAND_SECONDARY }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Notification Settings
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 300,
                      textAlign: 'center',
                      bgcolor: alpha(BRAND_SECONDARY, 0.02),
                      borderRadius: 2,
                      p: 4,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha(BRAND_SECONDARY, 0.1),
                        width: 80,
                        height: 80,
                        mb: 2,
                      }}
                    >
                      <BuildIcon
                        sx={{ color: BRAND_SECONDARY, fontSize: 40 }}
                      />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      Notification Center
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      Email, SMS, and push notification settings for students
                      and parents
                    </Typography>
                    <Chip
                      label="Coming Soon"
                      sx={{
                        bgcolor: alpha(BRAND_SECONDARY, 0.1),
                        color: BRAND_SECONDARY,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {tabValue === 3 && (
              <Card elevation={1} sx={{ bgcolor: '#fff' }}>
                <CardContent sx={{ p: 4 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ mb: 3 }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha('#FF9800', 0.1),
                        width: 40,
                        height: 40,
                      }}
                    >
                      <SecurityIcon sx={{ color: '#FF9800' }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Security Settings
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 300,
                      textAlign: 'center',
                      bgcolor: alpha('#FF9800', 0.02),
                      borderRadius: 2,
                      p: 4,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha('#FF9800', 0.1),
                        width: 80,
                        height: 80,
                        mb: 2,
                      }}
                    >
                      <BuildIcon sx={{ color: '#FF9800', fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      Security Controls
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      Password policies, two-factor authentication, and access
                      controls
                    </Typography>
                    <Chip
                      label="Coming Soon"
                      sx={{
                        bgcolor: alpha('#FF9800', 0.1),
                        color: '#FF9800',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {tabValue === 4 && (
              <Card elevation={1} sx={{ bgcolor: '#fff' }}>
                <CardContent sx={{ p: 4 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ mb: 3 }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha('#9C27B0', 0.1),
                        width: 40,
                        height: 40,
                      }}
                    >
                      <BackupIcon sx={{ color: '#9C27B0' }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      System Settings
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 300,
                      textAlign: 'center',
                      bgcolor: alpha('#9C27B0', 0.02),
                      borderRadius: 2,
                      p: 4,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha('#9C27B0', 0.1),
                        width: 80,
                        height: 80,
                        mb: 2,
                      }}
                    >
                      <BuildIcon sx={{ color: '#9C27B0', fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      System Administration
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      Backup & restore, system updates, and maintenance tools
                    </Typography>
                    <Chip
                      label="Coming Soon"
                      sx={{
                        bgcolor: alpha('#9C27B0', 0.1),
                        color: '#9C27B0',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}
          </Container>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default AdminSettings;
